import { createSlice } from '@reduxjs/toolkit';
import {
  createChangeRequest,
  clarifyChangeRequest,
  getChangeRequestResult,
  planChangeRequest,
  implementChangeRequestTask,
  getChangeRequest,
  editChangeRequestItem,
  deleteChangeRequestItem,
  addChangeRequestRow,
} from '../ActionTypes/changeRequestActionTypes';
import { ApiResponse, ChecklistItem } from '../../interfaces/changeRequest';
import { ChangeRequestSource } from '../../enums/changeRequest';
// import ActionError from '../../utils/ActionError';
import { ErrorType, SerializableError } from '../../utils/errorHandling';

interface ChangeRequestState {
  data: ApiResponse | null;
  loading: boolean;
  error: SerializableError | null;
  source: ChangeRequestSource | null;
  isImplementing: boolean;
}

const initialState: ChangeRequestState = {
  loading: false,
  error: null,
  data: null,
  source: null,
  isImplementing: false,
};

const changeRequestSlice = createSlice({
  name: 'changeRequest',
  initialState,
  reducers: {
    resetChangeRequest: () => {
      return initialState;
    },
    setChangeRequest: (state, action) => {
      if (state.data?.result && state.data.result.id === action.payload.id) {
        state.data.result = { ...state.data.result, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // getChangeRequest
      .addCase(getChangeRequest.pending, (state) => {
        state.loading = true;
        state.isImplementing = false;
      })
      .addCase(getChangeRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.isImplementing = false;
        state.data = { result: action.payload.data, status: 'Completed' };
        state.error = null;
      })
      .addCase(getChangeRequest.rejected, (state, action) => {
        state.loading = false;
        state.isImplementing = false;
        state.error = {
          message:
            ((action.payload || {}) as { message?: string }).message ||
            'Failed to fetch change request',
          type: ErrorType.UNKNOWN,
          isNetworkError: false,
        } as SerializableError;
        state.data = null;
      })
      // createChangeRequest
      .addCase(createChangeRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(createChangeRequest.fulfilled, (state, action) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createChangeRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = {
          message:
            ((action.payload || {}) as { message?: string }).message ||
            'Failed to create change request',
          type: ErrorType.UNKNOWN,
          isNetworkError: false,
        } as SerializableError;
      })
      // clarifyChangeRequest
      .addCase(clarifyChangeRequest.pending, (state) => {
        state.loading = true;
        state.source = ChangeRequestSource.ANALYSIS_CHECKLIST;
      })
      .addCase(clarifyChangeRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = {
          message:
            ((action.payload || {}) as { message?: string }).message ||
            'Failed to clarify change request',
          type: ErrorType.UNKNOWN,
          isNetworkError: false,
        } as SerializableError;
        state.source = action.meta.arg.source;
      })
      // planChangeRequest
      .addCase(planChangeRequest.pending, (state) => {
        state.loading = true;
        state.source = ChangeRequestSource.ACTIONS_EXECUTION;
      })
      .addCase(planChangeRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = {
          message:
            ((action.payload || {}) as { message?: string }).message ||
            'Failed to plan change request',
          type: ErrorType.UNKNOWN,
          isNetworkError: false,
        } as SerializableError;
        state.source = action.meta.arg.source;
      })
      // implementChangeRequestTask
      .addCase(implementChangeRequestTask.pending, (state, action) => {
        state.loading = true;
        state.isImplementing = true;
        state.source = action.meta.arg.source;
      })
      .addCase(implementChangeRequestTask.rejected, (state, action) => {
        state.loading = false;
        state.isImplementing = false;
        state.error = {
          message:
            ((action.payload || {}) as { message?: string }).message ||
            'Failed to implement change request',
          type: ErrorType.UNKNOWN,
          isNetworkError: false,
        } as SerializableError;
        state.source = action.meta.arg.source;
      })
      // getChangeRequestResult
      .addCase(getChangeRequestResult.fulfilled, (state, action) => {
        if (
          state.loading === (action.payload.data.status === 'in_progress') &&
          state.source === action.payload.source
        ) {
          return;
        }
        state.loading = action.payload.data.status === 'in_progress';
        if (
          action.payload?.source ===
            ChangeRequestSource.ACTIONS_IMPLEMENT_ALL ||
          action.payload?.source === ChangeRequestSource.ACTIONS_IMPLEMENT_ONE
        ) {
          // Checking if it's an implementation call and is in progress, then setting "Is Implementing" to true.
          state.isImplementing = action.payload.data.status === 'in_progress';
        }
        state.data = action.payload.data;
        state.error =
          action.payload.data.status === 'failed' && action.payload.data.error
            ? action.payload.data.error
            : null;
        state.source = action.meta.arg.source;
      })
      .addCase(getChangeRequestResult.rejected, (state, action) => {
        state.loading = false;
        state.isImplementing = false;
        state.error = {
          message:
            ((action.payload || {}) as { message?: string }).message ||
            'Failed to get change request result',
          type: ErrorType.UNKNOWN,
          isNetworkError: false,
        } as SerializableError;
        state.source = action.meta.arg.source;
      })
      // editChangeRequestItem
      .addCase(editChangeRequestItem.pending, (state, action) => {
        state.loading = true;
        state.source = action.meta.arg.source;
      })
      .addCase(editChangeRequestItem.fulfilled, (state, action) => {
        state.loading = false;
        state.data = { result: action.payload.data, status: 'Completed' };
        state.error = null;
        state.source = action.meta.arg.source;
      })
      .addCase(editChangeRequestItem.rejected, (state, action) => {
        state.loading = false;
        state.source = action.meta.arg.source;
      })
      .addCase(deleteChangeRequestItem.pending, (state, action) => {
        state.loading = true;
        state.source = action.meta.arg.source;
      })
      .addCase(deleteChangeRequestItem.fulfilled, (state, action) => {
        state.loading = false;
        state.data = { result: action.payload.data, status: 'Completed' };
        state.error = null;
        state.source = action.meta.arg.source;
      })
      .addCase(deleteChangeRequestItem.rejected, (state, action) => {
        state.loading = false;
        state.source = action.meta.arg.source;
      })
      // addChangeRequestRow
      .addCase(addChangeRequestRow.pending, (state, action) => {
        state.loading = true;
        state.source = action.meta.arg.source;
      })
      .addCase(addChangeRequestRow.fulfilled, (state, action) => {
        state.loading = false;
        const { toIndex, updatedValues } = action.payload.data;
        // Handle checklist items
        if (state.data?.result?.checklist?.items) {
          const items = [...state.data.result.checklist.items];
          items.splice(toIndex, 0, updatedValues as ChecklistItem);
          state.data.result.checklist.items = items;
        }
        state.error = null;
        state.source = action.meta.arg.source;
      })
      .addCase(addChangeRequestRow.rejected, (state, action) => {
        state.loading = false;
        state.error = {
          message:
            ((action.payload || {}) as { message?: string }).message ||
            'Failed to add change request row',
          type: ErrorType.UNKNOWN,
          isNetworkError: false,
        } as SerializableError;
        state.source = action.meta.arg.source;
      });
  },
});

export const { resetChangeRequest, setChangeRequest } =
  changeRequestSlice.actions;
export default changeRequestSlice.reducer;
